import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import { Modal } from "react-bootstrap";

import {
  CalendarToday as CalendarTodayIcon,
  CancelOutlined as CancelOutlinedIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Description as DescriptionIcon,
  FileCopy as FileCopyIcon,
  FormatListNumbered as FormatListNumberedIcon,
  Person as PersonIcon,
  Policy as PolicyIcon,
  PriceCheck as PriceCheckIcon,
  Print as PrintIcon,
  Share as ShareIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import formatDateTime from "../../../utils/dateFormate";

const StatusChip = styled(Chip)(({ theme, active }) => ({
  backgroundColor: active
    ? theme.palette.success.light
    : theme.palette.error.light,
  color: active ? theme.palette.success.dark : theme.palette.error.dark,
  fontWeight: 600,
  fontSize: "0.875rem",
}));

const InfoRow = ({ icon, label, value }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2.5 }}>
    <Box
      sx={{
        mr: 2,
        color: "text.secondary",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 32,
        height: 32,
        borderRadius: "50%",
        backgroundColor: "rgba(55, 176, 73, 0.1)",
        color: "#37B049",
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flex: 1 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 500, letterSpacing: 0.2 }}
      >
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 500 }}>
        {value || "Non renseigné"}
      </Typography>
    </Box>
  </Box>
);

const SectionTitle = ({ icon, children }) => (
  <Typography
    variant="h6"
    sx={{
      fontWeight: 600,
      mb: 2,
      color: "#37B049",
      display: "flex",
      alignItems: "center",
    }}
  >
    {icon && (
      <Box component="span" sx={{ mr: 1, display: "flex" }}>
        {icon}
      </Box>
    )}
    {children}
  </Typography>
);

const ProfilContrats = ({ show, handleClose, contrat }) => {
  if (!contrat) return null;

  const {
    id,
    assure_id,
    police_id,
    date_debut,
    date_fin,
    montant,
    statut,
    createdAt,
    assure,
    police,
    order,
  } = contrat;

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return formatDateTime(dateString);
  };

  return (
    <Modal
      size="lg"
      show={show}
      onHide={handleClose}
      aria-labelledby="contrat-modal-title"
      centered
      backdrop="static"
      dialogClassName="modal-90w"
    >
      <Paper elevation={3} sx={{ overflow: "hidden" }}>
        <Modal.Header closeButton style={{ border: "none", paddingBottom: 0 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: "#37B049",
                  boxShadow: "0 3px 8px rgba(55, 176, 73, 0.25)",
                }}
              >
                <PolicyIcon fontSize="large" />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight="bold" color="#37B049">
                  Contrat #{id}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Référence:</strong>{" "}
                  {police?.numero || "Non renseigné"}
                </Typography>
              </Box>
            </Box>
            <StatusChip
              icon={
                statut ? (
                  <CheckCircleOutlineIcon fontSize="small" />
                ) : (
                  <CancelOutlinedIcon fontSize="small" />
                )
              }
              label={statut ? "Actif" : "Inactif"}
              active={statut}
            />
          </Box>
        </Modal.Header>

        <Modal.Body style={{ overflowY: "auto", padding: "20px" }}>
          <Grid container spacing={3}>
            {/* Informations sur l'assuré */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  height: "100%",
                  background:
                    "linear-gradient(135deg, rgba(55, 176, 73, 0.05) 0%, rgba(255, 255, 255, 0) 100%)",
                }}
              >
                <SectionTitle icon={<PersonIcon />}>
                  Informations Assuré
                </SectionTitle>

                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Avatar
                    sx={{
                      width: 64,
                      height: 64,
                      bgcolor: "#37B049",
                      mr: 2,
                      fontSize: "1.5rem",
                    }}
                  >
                    {assure?.nom?.charAt(0)}
                    {assure?.prenom?.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {assure?.nom} {assure?.prenom}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ID Assuré: {assure_id}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Ajoutez ici d'autres informations sur l'assuré si disponible */}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Pour plus de détails sur l'assuré, veuillez consulter son
                  profil complet.
                </Typography>
              </Paper>
            </Grid>

            {/* Informations sur la police */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  height: "100%",
                  background:
                    "linear-gradient(135deg, rgba(55, 176, 73, 0.05) 0%, rgba(255, 255, 255, 0) 100%)",
                }}
              >
                <SectionTitle icon={<PolicyIcon />}>
                  Détails de la Police
                </SectionTitle>

                <InfoRow
                  icon={<PolicyIcon fontSize="small" />}
                  label="Nom de Police"
                  value={police?.label}
                />

                <InfoRow
                  icon={<FormatListNumberedIcon fontSize="small" />}
                  label="Numéro de Police"
                  value={police?.numero}
                />

                <InfoRow
                  icon={<DescriptionIcon fontSize="small" />}
                  label="Description"
                  value={police?.description}
                />

                <InfoRow
                  icon={<CalendarTodayIcon fontSize="small" />}
                  label="Police créée le"
                  value={formatDate(police?.createdAt)}
                />
              </Paper>
            </Grid>

            {/* Détails du contrat */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  background:
                    "linear-gradient(135deg, rgba(55, 176, 73, 0.05) 0%, rgba(255, 255, 255, 0) 100%)",
                }}
              >
                <SectionTitle icon={<FileCopyIcon />}>
                  Détails du Contrat
                </SectionTitle>

                <Box sx={{ display: "flex", flexWrap: "wrap" }}>
                  <Box sx={{ flex: "1 1 300px", pr: { xs: 0, md: 3 }, mb: 3 }}>
                    <InfoRow
                      icon={<CalendarTodayIcon fontSize="small" />}
                      label="Date de début"
                      value={formatDate(date_debut)}
                    />

                    <InfoRow
                      icon={<CalendarTodayIcon fontSize="small" />}
                      label="Date de fin"
                      value={
                        date_fin
                          ? formatDate(date_fin)
                          : "Contrat sans date de fin"
                      }
                    />
                  </Box>

                  <Box sx={{ flex: "1 1 300px" }}>
                    <InfoRow
                      icon={<PriceCheckIcon fontSize="small" />}
                      label="Montant"
                      value={montant ? `${montant} FCFA` : "Non spécifié"}
                    />

                    <InfoRow
                      icon={<CalendarTodayIcon fontSize="small" />}
                      label="Date de création"
                      value={formatDate(createdAt)}
                    />
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Modal.Body>

        <Modal.Footer style={{ border: "none", paddingTop: 0 }}>
          <Stack
            direction="row"
            spacing={1}
            sx={{ width: "100%", justifyContent: "space-between" }}
          >
            <Box>
              <Tooltip title="Imprimer le contrat">
                <IconButton>
                  <PrintIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Partager">
                <IconButton>
                  <ShareIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Button
              variant="contained"
              onClick={handleClose}
              sx={{
                backgroundColor: "#37B049",
                boxShadow: "0 4px 12px rgba(55, 176, 73, 0.3)",
                "&:hover": {
                  backgroundColor: "#2e8b3a",
                  boxShadow: "0 6px 14px rgba(55, 176, 73, 0.4)",
                },
                px: 4,
                py: 1,
              }}
            >
              Fermer
            </Button>
          </Stack>
        </Modal.Footer>
      </Paper>
    </Modal>
  );
};

export default ProfilContrats;
